import { useState, useEffect } from 'react';
import { get } from '../helpers/apiHelper';

export const useDashboardData = (userId) => {
    const [marcasStats, setMarcasStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [aiInsights, setAiInsights] = useState(null);
    // const [businessAnalysis, setBusinessAnalysis] = useState(null);
    const [ultimaEstrategia, setUltimaEstrategia] = useState(null);
    const [fechaClaveProxima, setFechaClaveProxima] = useState(null);
    const [priorityObjective, setPriorityObjective] = useState(null);

    // Nuevas métricas del dashboard
    const [dashboardMetrics, setDashboardMetrics] = useState({
        nuevosClientes: 0,
        ticketPromedio: 0,
        ventasTotales: 0,
        crecimiento: {
            nuevosClientes: 0,
            ticketPromedio: 0,
            ventasTotales: 0
        }
    });





    // Funciones auxiliares para cálculos de métricas
    const getMonthName = (monthString) => {
        const monthNames = {
            "01": "Enero", "02": "Febrero", "03": "Marzo", "04": "Abril",
            "05": "Mayo", "06": "Junio", "07": "Julio", "08": "Agosto",
            "09": "Septiembre", "10": "Octubre", "11": "Noviembre", "12": "Diciembre"
        };
        return monthNames[monthString.split('-')[1]];
    };

    const getPreviousMonth = (currentMonth) => {
        const [year, month] = currentMonth.split('-').map(Number);
        const date = new Date(year, month - 1, 1);
        date.setMonth(date.getMonth() - 1);
        return date.toISOString().slice(0, 7);
    };

    const calculateGrowthRate = (current, previous) => {
        if (previous === 0) return current > 0 ? 100 : 0;
        return Math.round(((current - previous) / previous) * 100 * 100) / 100;
    };

    // Función para cargar métricas del dashboard
    const loadDashboardMetrics = async (marcaId) => {
        try {
            const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
            const previousMonth = getPreviousMonth(currentMonth);
            const currentMonthName = getMonthName(currentMonth);
            const previousMonthName = getMonthName(previousMonth);

            // Obtener métricas en paralelo
            const [
                combinedStats,
                monthlySales,
                monthlyAverageTicket
            ] = await Promise.allSettled([
                get(`statistics/combined?marcaIds=${marcaId}`),
                get(`statistics/sales/monthly/${marcaId}`),
                get(`statistics/average-ticket/monthly/${marcaId}`)
            ]);

            // Procesar nuevos clientes del mes actual
            const nuevosClientes = combinedStats.status === 'fulfilled'
                ? combinedStats.value[0]?.fidelStats?.nuevosClientesMes || 0
                : 0;

            // Procesar ventas del mes actual
            const ventasMesActual = monthlySales.status === 'fulfilled'
                ? monthlySales.value.find(month => month.month === currentMonthName)?.sales || 0
                : 0;

            // Procesar ticket promedio del mes actual
            const ticketPromedioMesActual = monthlyAverageTicket.status === 'fulfilled'
                ? monthlyAverageTicket.value.find(month => month.month === currentMonthName)?.avgTicket || 0
                : 0;

            // Procesar métricas del mes anterior para calcular crecimiento
            const ventasMesAnterior = monthlySales.status === 'fulfilled'
                ? monthlySales.value.find(month => month.month === previousMonthName)?.sales || 0
                : 0;

            const ticketPromedioMesAnterior = monthlyAverageTicket.status === 'fulfilled'
                ? monthlyAverageTicket.value.find(month => month.month === previousMonthName)?.avgTicket || 0
                : 0;

            // Calcular crecimiento
            const crecimientoVentas = calculateGrowthRate(ventasMesActual, ventasMesAnterior);
            const crecimientoTicketPromedio = calculateGrowthRate(ticketPromedioMesActual, ticketPromedioMesAnterior);
            const crecimientoNuevosClientes = calculateGrowthRate(nuevosClientes, 0); // Asumiendo que no tenemos datos del mes anterior

            setDashboardMetrics({
                nuevosClientes,
                ticketPromedio: ticketPromedioMesActual,
                ventasTotales: ventasMesActual,
                crecimiento: {
                    nuevosClientes: crecimientoNuevosClientes,
                    ticketPromedio: crecimientoTicketPromedio,
                    ventasTotales: crecimientoVentas
                }
            });

        } catch (error) {
            console.error('Error al cargar métricas del dashboard:', error);
            // Establecer valores por defecto en caso de error
            setDashboardMetrics({
                nuevosClientes: 0,
                ticketPromedio: 0,
                ventasTotales: 0,
                crecimiento: {
                    nuevosClientes: 0,
                    ticketPromedio: 0,
                    ventasTotales: 0
                }
            });
        }
    };

    // const fetchBusinessAnalysis = async () => {
    //     try {
    //         const analysis = await get(`ai-campaign-strategy/business-analysis/${userId}`);
    //         setBusinessAnalysis(analysis);
    //     } catch (error) {
    //         console.error('Error al obtener análisis de negocio:', error);
    //         // No establecer análisis por defecto ya que no se usa en el dashboard
    //     }
    // };





    const determinePriorityObjective = (marca) => {
        const stats = marca.fidelStats;
        const basicStats = marca.basicStats;

        if (!stats || !basicStats) {
            setPriorityObjective({
                type: 'general',
                title: 'Optimizar tu Marketing con IA',
                description: 'Comienza a usar PetitPrime para crear campañas inteligentes y hacer crecer tu negocio',
                icon: 'brain',
                action: 'Crear primera campaña IA',
                color: 'bg-gradient-to-r from-blue-500 to-indigo-600'
            });
            return;
        }

        // Lógica para determinar objetivo prioritario basado en marketing
        const nuevosClientes = stats.nuevosClientesMes || 0;
        const totalClientes = basicStats.totalClientes || 0;
        const clientesInactivos = stats.clientesInactivosRecuperados || 0;

        if (nuevosClientes < 10) {
            setPriorityObjective({
                type: 'acquisition',
                title: 'Atraer nuevos clientes',
                description: `Solo ${nuevosClientes} nuevos clientes este mes. La IA puede ayudarte a crear campañas de captación efectivas.`,
                icon: 'crosshairs',
                action: 'Crear campaña de captación IA',
                color: 'bg-gradient-to-r from-green-500 to-teal-600',
                urgency: 'high'
            });
        } else if (clientesInactivos < totalClientes * 0.1) {
            setPriorityObjective({
                type: 'retention',
                title: 'Recuperar clientes inactivos',
                description: `${totalClientes - clientesInactivos} clientes no han vuelto. La IA puede crear estrategias de re-engagement.`,
                icon: 'rocket',
                action: 'Crear campaña de retención IA',
                color: 'bg-gradient-to-r from-purple-500 to-pink-600',
                urgency: 'medium'
            });
        } else {
            setPriorityObjective({
                type: 'growth',
                title: 'Aumentar ventas y conversiones',
                description: 'Tus clientes están activos. La IA puede optimizar tus campañas para maximizar ventas.',
                icon: 'chart-line',
                action: 'Crear campaña de ventas IA',
                color: 'bg-gradient-to-r from-orange-500 to-red-600',
                urgency: 'low'
            });
        }
    };

    useEffect(() => {
        const loadData = async () => {
            if (userId) {
                try {
                    setLoading(true);

                    // Cargar datos en paralelo
                    const [marcasResponse, ultimaEstrategiaData, fechaClaveData] = await Promise.allSettled([
                        get('marcas'),
                        get('asistente-configuracion/ultima').catch(() => null),
                        get('fechas-clave/proximas?dias=30').catch(() => [])
                    ]);

                    // Procesar marcas
                    if (marcasResponse.status === 'fulfilled' && marcasResponse.value) {
                        const responseMarcas = marcasResponse.value;
                        if (responseMarcas.length > 0) {
                            const marcaIds = responseMarcas.map((m) => m._id).join(',');
                            try {
                                const combinedData = await get(`statistics/combined?marcaIds=${marcaIds}`);
                                const mergedMarcas = responseMarcas.map((marca) => {
                                    const stats = combinedData.find((item) => item.marcaId === marca._id);
                                    return {
                                        ...marca,
                                        basicStats: stats?.basicStats,
                                        fidelStats: stats?.fidelStats,
                                    };
                                });
                                setMarcasStats(mergedMarcas);

                                // Cargar métricas del dashboard para la primera marca
                                if (mergedMarcas.length > 0) {
                                    await loadDashboardMetrics(mergedMarcas[0]._id);
                                    determinePriorityObjective(mergedMarcas[0]);
                                }
                            } catch (error) {
                                console.error('Error al obtener estadísticas combinadas:', error);
                                setMarcasStats(responseMarcas);
                            }
                        } else {
                            setMarcasStats([]);
                        }
                    }

                    // Procesar última estrategia
                    if (ultimaEstrategiaData.status === 'fulfilled' && ultimaEstrategiaData.value) {
                        setUltimaEstrategia(ultimaEstrategiaData.value);
                    } else {
                        // Intentar obtener de marketing chain usando el nuevo endpoint
                        try {
                            const lastStrategy = await get(`agents/marketing-chain/last-strategy/${userId}`);
                            if (lastStrategy) {
                                const formattedStrategy = {
                                    descripcion: `Estrategia: ${lastStrategy.selectedStrategy?.title || 'Marketing Inteligente'}`,
                                    fechaClave: {
                                        fecha: lastStrategy.createdAt,
                                        nombre: 'Estrategia IA Generada'
                                    },
                                    tipo: 'marketing_chain',
                                    sessionId: lastStrategy.sessionId,
                                    status: lastStrategy.status || 'completed',
                                    createdAt: lastStrategy.createdAt,
                                    objective: lastStrategy.selectedObjective,
                                    strategy: lastStrategy.selectedStrategy,
                                    tactics: lastStrategy.tactics,
                                    analysis: lastStrategy.analysis,
                                    temporalAnalysis: lastStrategy.temporalAnalysis
                                };

                                setUltimaEstrategia(formattedStrategy);
                            }
                        } catch (error) {
                            console.error('Error al obtener última estrategia de marketing chain:', error);
                        }
                    }

                    // Procesar fecha clave
                    if (fechaClaveData.status === 'fulfilled' && fechaClaveData.value) {
                        const proximasMismoPais = fechaClaveData.value.filter(fc => fc.pais === 'España');
                        if (proximasMismoPais.length > 0) {
                            setFechaClaveProxima(proximasMismoPais[0]);
                        }
                    }

                    // Establecer insights por defecto
                    setAiInsights({
                        opportunities: [
                            'Implementar campañas de email marketing automatizadas',
                            'Crear programas de fidelización con puntos',
                            'Optimizar la experiencia del cliente en tienda',
                            'Desarrollar estrategias de retención personalizadas'
                        ],
                        recommendations: [
                            'Analizar datos de ventas para identificar patrones',
                            'Segmentar clientes por comportamiento de compra',
                            'Implementar estrategias de retención personalizadas',
                            'Optimizar el timing de las campañas'
                        ]
                    });

                } catch (error) {
                    console.error('Error general al cargar datos:', error);
                } finally {
                    setLoading(false);
                }
            }
        };

        loadData();
    }, [userId]); // Solo depende de userId

    return {
        marcasStats,
        loading,
        aiInsights,
        // businessAnalysis,
        ultimaEstrategia,
        fechaClaveProxima,
        priorityObjective,
        dashboardMetrics
    };
}; 